import { CircularProgress, FormControl, MenuItem, Select } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SuccessModal from "../../../../components/element/SuccessModal";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { useConfiguration } from "../../../../services/ConfigurationProvider";
import { StorageContext } from "../../../../services/StorageProvider";
import {
  getDiplomatsPassenger,
  getGovernmentPassenger,
  getInfantPassenger,
  getMilitaryPassenger,
} from "../../../../utils/configUtils";
import { DISPUTE_DATE_AND_TIME_FORMAT } from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import {
  getAirportCode,
  getAirportData,
  getFlightNumber,
  handleEmptyValue,
} from "../../../../utils/utils";
import AddPassengerCountDialog from "../../Flight Roster/Edit Flight Roster/Views/AddPassengerCountDialog";
import "../PassengerDisputeCount.css";
import AirlineRejectModal from "../Views/AirlineRejectModal";
import { useFormData } from "../../../../helpers/FormDataProvider";
import AcceptDisputeModal from "../Views/AcceptDisputeModal";
import ErrorSnackbarAlert from "../../../../components/element/ErrorSnackbarAlert";
import VerificationIcon from "../../../../components/element/VerificationIcon";

// Component to display dispute reason with expand/collapse functionality
const DisputeReasonDisplay = ({ reason }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const textRef = React.useRef(null);

  React.useEffect(() => {
    if (textRef.current) {
      // Check if text overflows when collapsed (not expanded)
      // We need to check the collapsed state to determine if we need the toggle
      const tempElement = textRef.current.cloneNode(true);
      tempElement.className = "dispute-reason-text collapsed";
      tempElement.style.visibility = "hidden";
      tempElement.style.position = "absolute";
      tempElement.style.top = "-9999px";
      document.body.appendChild(tempElement);

      const isOverflowing = tempElement.scrollWidth > tempElement.clientWidth;
      setShowToggle(isOverflowing);

      document.body.removeChild(tempElement);
    }
  }, [reason]);

  if (!reason || reason.trim() === "") {
    return <span className="dispute-reason-no-data">-</span>;
  }

  return (
    <div className="dispute-reason-display">
      <div
        ref={textRef}
        className={`dispute-reason-text ${
          isExpanded ? "expanded" : "collapsed"
        }`}
      >
        {reason}
      </div>
      {showToggle && (
        <button
          className="dispute-reason-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show Less" : "Show More"}
        </button>
      )}
    </div>
  );
};
const DisputeDetailsTable = (disputeData) => {
  const { airportData, setAirportData } = useContext(StorageContext);
  const navigate = useNavigate();
  const { configurationData } = useConfiguration();
  const { userData } = useFormData();
  const [airlineNames, setAirlineNames] = useState({});
  const [departureAirportName, setDepartureAirportName] = useState("");
  const [arrivalAirportName, setArrivalAirportName] = useState("");
  // Local state to hold the dispute data that can be updated
  const [localDisputeData, setLocalDisputeData] = useState(
    disputeData?.disputeData
  );

  const [selectKey, setSelectKey] = useState(0);
  const [selectedAction, setSelectedAction] = useState("");

  const {
    acceptDispute,
    rejectDispute,
    updateDisputePassengerCount,
    acceptAAPassengerCount,
    acceptAirlinePassengerCount,
    getAirlineById,
    fetchAirportById,
  } = useAirlineAdminApi(false);

  // Load cached airline names from localStorage on component mount
  useEffect(() => {
    const cachedNames = localStorage.getItem("airlineNames");
    if (cachedNames) {
      try {
        setAirlineNames(JSON.parse(cachedNames));
      } catch (error) {
        console.error("Error parsing cached airline names:", error);
      }
    }
  }, []);

  // Fetch airline name from API if not in cache
  useEffect(() => {
    const fetchAirlineName = async () => {
      const airlineId = localDisputeData?.airlineId;
      if (!airlineId) return;

      let name = airlineNames[airlineId];

      if (name === undefined && airlineId) {
        try {
          const airline = await getAirlineById(airlineId);
          name = airline?.data?.airlineName || "";

          // Update state and localStorage with the fetched name
          setAirlineNames((prev) => {
            const updated = { ...prev, [airlineId]: name };
            localStorage.setItem("airlineNames", JSON.stringify(updated));
            return updated;
          });
        } catch (error) {
          console.error("Error fetching airline name:", error);
        }
      }
    };

    fetchAirlineName();
  }, [airlineNames, localDisputeData?.airlineId, getAirlineById]);

  // Update local state when props change
  useEffect(() => {
    setLocalDisputeData(disputeData?.disputeData);
  }, [disputeData?.disputeData]);

  const governmentPassengerEnabled = getGovernmentPassenger(configurationData);
  const diplomatsPassengerEnabled = getDiplomatsPassenger(configurationData);
  const militaryPassengerEnabled = getMilitaryPassenger(configurationData);
  const infantPassengerEnabled = getInfantPassenger(configurationData);

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    action: null,
    disputeData: null,
  });
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    message: "",
    action: null,
  });
  const [editDialog, setEditDialog] = useState({
    isOpen: false,
    disputeData: null,
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });

  const handleActionChange = (event) => {
    const action = event.target.value;
    setSelectedAction(action);

    if (action && action !== "") {
      setConfirmationModal({
        isOpen: true,
        action: action,
        disputeData: disputeData,
      });
    }
  };

  // Mutations for API calls
  const acceptBMSMutation = useMutation({
    mutationFn: (disputeId) => acceptAAPassengerCount(disputeId),
    onSuccess: (response) => {
      setConfirmationModal({ isOpen: false, action: null, disputeData: null });
      setSuccessModal({
        isOpen: true,
        message:
          response?.data?.data || "BMS Passenger Count accepted successfully!",
        action: "accept-bms",
      });
    },
    onError: (error) => {
      setErrorSnackbar({
        open: true,
        message:
          error.message ||
          "An error occurred while accepting BMS passenger count",
      });
    },
  });

  const acceptAirlineMutation = useMutation({
    mutationFn: (disputeId) => acceptAirlinePassengerCount(disputeId),
    onSuccess: (response) => {
      setConfirmationModal({ isOpen: false, action: null, disputeData: null });
      setSuccessModal({
        isOpen: true,
        message:
          response?.data || "Airline Passenger Count accepted successfully!",
        action: "accept-airline",
      });
    },
    onError: (error) => {
      setErrorSnackbar({
        open: true,
        message:
          error.message ||
          "An error occurred while accepting airline passenger count",
      });
    },
  });
  const acceptDisputeMutation = useMutation({
    mutationFn: ({ disputeId }) => acceptDispute(disputeId),
    onSuccess: (response) => {
      setConfirmationModal({ isOpen: false, action: null, disputeData: null });
      setSuccessModal({
        isOpen: true,
        message: response?.data?.data ?? "Disputed accepted successfully!",
        action: "accept",
      });
    },
    onError: (error) => {
      console.error("Error accepting dispute:", error);
    },
  });

  const rejectDisputeMutation = useMutation({
    mutationFn: ({ disputeId }) => rejectDispute(disputeId),
    onSuccess: (response) => {
      setConfirmationModal({ isOpen: false, action: null, disputeData: null });
      setSuccessModal({
        isOpen: true,
        message: response?.data?.data ?? "Disputed rejected successfully!",
        action: "reject",
      });
    },
    onError: (error) => {
      console.error("Error rejecting dispute:", error);
    },
  });

  const updatePassengerCountMutation = useMutation({
    mutationFn: ({ planId, disputeData }) =>
      updateDisputePassengerCount(planId, disputeData),
    onSuccess: (response, variables) => {
      handleCloseEditDialog();

      // Update the local state with new count values
      setLocalDisputeData((prev) => ({
        ...prev,
        airlinePassengerCount: variables.disputeData.airlinePassengerCount,
        airlineInfant: variables.disputeData.airlineInfant,
        airlineMilitary: variables.disputeData.airlineMilitary,
        airlineGovernment: variables.disputeData.airlineGovernment,
        airlineDiplomats: variables.disputeData.airlineDiplomats,
        airlineExemptedPassengerCount:
          (variables.disputeData.airlineInfant || 0) +
          (variables.disputeData.airlineMilitary || 0) +
          (variables.disputeData.airlineGovernment || 0) +
          (variables.disputeData.airlineDiplomats || 0),
      }));

      // Update the local state with new count values
      setLocalDisputeData((prev) => ({
        ...prev,
        airlinePassengerCount: variables.disputeData.airlinePassengerCount,
        airlineInfant: variables.disputeData.airlineInfant,
        airlineMilitary: variables.disputeData.airlineMilitary,
        airlineGovernment: variables.disputeData.airlineGovernment,
        airlineDiplomats: variables.disputeData.airlineDiplomats,
        airlineExemptedPassengerCount:
          (variables.disputeData.airlineInfant || 0) +
          (variables.disputeData.airlineMilitary || 0) +
          (variables.disputeData.airlineGovernment || 0) +
          (variables.disputeData.airlineDiplomats || 0),
      }));

      setSuccessModal({
        isOpen: true,
        message: response?.data ?? "Dispute resolved successfully",
        action: "update",
      });
    },
  });

  const handleAcceptDispute = (row) => {
    if (!row?.id) {
      console.error("Dispute ID is required");
      return;
    }

    setConfirmationModal({
      isOpen: true,
      action: "accept",
      disputeData: row,
    });
  };

  const handleRejectDispute = (row) => {
    if (!row?.id) {
      console.error("Dispute ID is required");
      return;
    }

    setConfirmationModal({
      isOpen: true,
      action: "reject",
      disputeData: row,
    });
  };

  const handleConfirmAction = () => {
    const { action, disputeData } = confirmationModal;
    if (!disputeData?.id) return;
    setConfirmationModal({ isOpen: false, action: null, disputeData: null });

    if (action === "accept") {
      acceptDisputeMutation.mutate({ disputeId: disputeData.id });
    } else if (action === "reject") {
      rejectDisputeMutation.mutate({ disputeId: disputeData.id });
    }
  };

  const handleAAConfirmAction = () => {
    const { action, disputeData } = confirmationModal;

    if (!disputeData?.disputeData?.id) return;

    if (action === "accept-bms") {
      acceptBMSMutation.mutate(disputeData?.disputeData?.id);
    } else if (action === "accept-airline") {
      acceptAirlineMutation.mutate(disputeData?.disputeData?.id);
    }
  };
  const handleCloseConfirmationModal = () => {
    setConfirmationModal({ isOpen: false, action: null, disputeData: null });
    setSelectedAction(""); // Reset dropdown selection
    setSelectKey((prev) => prev + 1); // Reset select component
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal({ isOpen: false, message: "", action: null });
    setSelectedAction(""); // Reset dropdown selection
    setSelectKey((prev) => prev + 1); // Reset select component
    navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER);
  };

  const handleEditDispute = (row) => {
    setEditDialog({
      isOpen: true,
      disputeData: row,
    });
  };

  const handleCloseEditDialog = () => {
    setEditDialog({
      isOpen: false,
      disputeData: null,
    });
  };
  const handleCloseErrorSnackbar = () => {
    setErrorSnackbar({ open: false, message: "" });
  };

  const handleSaveEditDialog = (passengerCounts) => {
    // Close the dialog after saving
    const addPassengerCountData = {
      airlinePassengerCount: passengerCounts?.totalBoarded,
      airlineInfant: passengerCounts?.infant,
      airlineMilitary: passengerCounts?.military,
      airlineGovernment: passengerCounts?.government,
      airlineDiplomats: passengerCounts?.diplomat,
    };
    updatePassengerCountMutation.mutate({
      planId: editDialog.disputeData?.id,
      disputeData: addPassengerCountData,
    });
  };
  const getAirportName = async (airportId) => {
    // Validate airportId exists before proceeding
    if (!airportId) {
      return "";
    }

    const airport = getAirportData(airportData, airportId);
    if (airport === null) {
      // Fetch from API if not in local cache
      const response = await fetchAirportById(airportId);

      // Cache the fetched airport in local storage for future use
      if (response && setAirportData) {
        setAirportData((prevAirportData) => [
          ...(prevAirportData || []),
          response,
        ]);
      }

      return getAirportCode(response);
    }
    return getAirportCode(airport);
  };

  useEffect(() => {
    const fetchAirportNames = async () => {
      // Only call API if local data is not available
      const depName = await getAirportName(
        localDisputeData?.departureAirportId
      );
      const arrName = await getAirportName(localDisputeData?.arrivalAirportId);
      setDepartureAirportName(depName);
      setArrivalAirportName(arrName);
    };

    fetchAirportNames();
  }, [
    localDisputeData?.departureAirportId,
    localDisputeData?.arrivalAirportId,
    airportData,
  ]);

  return (
    <div className="dispute-details-table-parent-container">
      <div className="dispute-details-table-container">
        {userData?.isAAUser &&
          (localDisputeData?.adsbVerified ||
            (localDisputeData?.bmsVerified && (
              <div className="dispute-details-verification-icon-divider-row">
                <VerificationIcon
                  adsbVerified={localDisputeData?.adsbVerified}
                  bmsVerified={localDisputeData?.bmsVerified}
                />
              </div>
            )))}
        <table className="dispute-details-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Flight Number</th>
              <th>Airline Name</th>
              <th>Origin</th>
              <th>Destination</th>
              <th>Arrival Date and Time</th>
              <th>Departure Date and Time</th>
              {userData.isAAUser && (
                <>
                  <th>BMS Total Passenger</th>
                  <th>BMS Total Exempted Passenger</th>
                  {infantPassengerEnabled && <th>BMS Exempted Infant</th>}
                  {militaryPassengerEnabled && <th>BMS Exempted Military</th>}
                  {governmentPassengerEnabled && <th>BMS Exempted GOV</th>}
                  {diplomatsPassengerEnabled && <th>BMS Exempted DIP</th>}
                </>
              )}
              <th>Airline Total Passenger</th>
              <th>Airline Total Exempted Passenger</th>
              {infantPassengerEnabled && <th>Airline Exempted Infant</th>}
              {militaryPassengerEnabled && <th>Airline Exempted Military</th>}
              {governmentPassengerEnabled && <th>Airline Exempted GOV</th>}
              {diplomatsPassengerEnabled && <th>Airline Exempted DIP</th>}
              <th>Airline Crew Members</th>
              <th>Billable Passenger</th>
              <th>Disputed Reason</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr key={localDisputeData?.id}>
              <td>{1}</td>
              <td>{getFlightNumber(localDisputeData)}</td>
              <td>
                {handleEmptyValue(
                  airlineNames[localDisputeData?.airlineId] ||
                    localDisputeData?.airlineName
                )}
              </td>
              <td>{departureAirportName}</td>
              <td>{arrivalAirportName}</td>
              <td>
                {dayjs(localDisputeData?.arrivalTime).format(
                  DISPUTE_DATE_AND_TIME_FORMAT
                )}
              </td>
              <td>
                {dayjs(localDisputeData?.departureTime).format(
                  DISPUTE_DATE_AND_TIME_FORMAT
                )}
              </td>
              {userData.isAAUser && (
                <>
                  <td>{localDisputeData?.scannedPassengerCount}</td>
                  <td>{localDisputeData?.exemptedPassengerCount}</td>
                  {infantPassengerEnabled && (
                    <td>{localDisputeData?.scannedInfant}</td>
                  )}
                  {militaryPassengerEnabled && (
                    <td>{localDisputeData?.scannedMilitary}</td>
                  )}
                  {governmentPassengerEnabled && (
                    <td>{localDisputeData?.scannedGovernment}</td>
                  )}
                  {diplomatsPassengerEnabled && (
                    <td>{localDisputeData?.scannedDiplomats}</td>
                  )}
                </>
              )}
              <td>{localDisputeData?.airlinePassengerCount}</td>
              <td>{localDisputeData?.airlineExemptedPassengerCount}</td>
              {infantPassengerEnabled && (
                <td>{localDisputeData?.airlineInfant}</td>
              )}
              {militaryPassengerEnabled && (
                <td>{localDisputeData?.airlineMilitary}</td>
              )}
              {governmentPassengerEnabled && (
                <td>{localDisputeData?.airlineGovernment}</td>
              )}
              {diplomatsPassengerEnabled && (
                <td>{localDisputeData?.airlineDiplomats}</td>
              )}
              <td>
                {localDisputeData?.deadheadCrews &&
                localDisputeData?.deadheadCrews?.length > 0 ? (
                  <div className="dispute-details-crew-members-list">
                    {localDisputeData?.deadheadCrews?.map((crew, index) => (
                      <div
                        key={index}
                        className="dispute-details-crew-member-item"
                      >
                        <div className="dispute-details-crew-member-name">
                          {crew.passengerName} - {crew.seatNumber}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>{localDisputeData?.airlineDeadheadCrewCount || 0}</p>
                )}
              </td>
              <td>
                {localDisputeData?.airlinePassengerCount -
                  localDisputeData?.airlineExemptedPassengerCount}
              </td>
              <td>
                <div className="dispute-details-table-dispute-reason">
                  {localDisputeData?.disputeReason ? (
                    <DisputeReasonDisplay
                      reason={localDisputeData?.disputeReason}
                    />
                  ) : (
                    <span className="dispute-reason-no-data">-</span>
                  )}
                </div>
              </td>
              <td>
                <div className="dispute-details-table-action-buttons-container">
                  {/* TODO:This is will be removed or finalized after confirmation. */}

                  {userData.isAAUser ? (
                    <FormControl size="small">
                      <Select
                        key={selectKey}
                        value={selectedAction}
                        onChange={handleActionChange}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected || selected === "") {
                            return (
                              <span style={{ color: "#333" }}>
                                Select Action
                              </span>
                            );
                          }
                          if (selected === "accept-bms") {
                            return "Accept BMS Passenger Count";
                          }
                          if (selected === "accept-airline") {
                            return "Accept Airline Passenger Count";
                          }
                          return selected;
                        }}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                        }}
                        sx={{
                          minWidth: 250,
                          "& .MuiSelect-select": {
                            paddingTop: "8px",
                            paddingBottom: "8px",
                          },
                        }}
                      >
                        <MenuItem value="">Select Action</MenuItem>
                        <MenuItem value="accept-bms">
                          Accept BMS Passenger Count
                        </MenuItem>
                        <MenuItem value="accept-airline">
                          Accept Airline Passenger Count
                        </MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <button
                      className="dispute-details-table-accept-button"
                      onClick={() => handleEditDispute(localDisputeData)}
                      disabled={
                        acceptDisputeMutation.isPending ||
                        rejectDisputeMutation.isPending
                      }
                    >
                      {acceptDisputeMutation.isPending ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        "Correct boarded count"
                      )}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      <AirlineRejectModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCloseConfirmationModal}
        onDelete={handleConfirmAction}
        message={
          confirmationModal.action === "accept"
            ? "Do you want to accept this dispute?"
            : "Do you want to reject this dispute?"
        }
      />

      <AcceptDisputeModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCloseConfirmationModal}
        onDelete={handleAAConfirmAction}
        isLoading={
          acceptBMSMutation.isPending || acceptAirlineMutation.isPending
        }
        message={
          confirmationModal.action === "accept-bms"
            ? "Do you want to accept BMS Passenger Count?"
            : confirmationModal.action === "accept-airline"
            ? "Do you want to accept Airline Passenger Count?"
            : "Do you want to proceed with this action?"
        }
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={handleCloseSuccessModal}
        title="Success"
        message={successModal.message}
        buttonLabel="OK"
      />

      {/* Edit Passenger Count Dialog */}
      <AddPassengerCountDialog
        isOpen={editDialog.isOpen}
        onClose={handleCloseEditDialog}
        onSave={handleSaveEditDialog}
        initialData={editDialog.disputeData}
        isLoading={updatePassengerCountMutation?.isPending}
      />
      <ErrorSnackbarAlert
        open={errorSnackbar.open}
        onClose={handleCloseErrorSnackbar}
      >
        {errorSnackbar.message}
      </ErrorSnackbarAlert>
    </div>
  );
};

export default DisputeDetailsTable;
